import { useEffect, useState } from "react";
import dayjs from "@shared/dayjs";

import { getCallLogs, getTextLogs, getCallLogCount, getTextLogCount } from "./services";
import { getTrackingNumberIds } from "./helpers";
import formatPhoneNumber from "../../shared/FormatPhoneNumber";
import { useCurrentUser } from "../../reducers/layoutReducer/selectors";

const mapCallLogs = (callLogs, timeZone) =>
  callLogs?.map((log) => {
    let outcome = "Missed";

    if (log.voicemail_url !== null) {
      outcome = "Voicemail";
    } else if (log.outcome === "completed") {
      outcome = "Talked";
    }

    return {
      callLogId: log.id,
      answererFullName: log.answerer_full_name,
      answererAvatarUrl: log.answerer_avatar_url?.length > 3 ? log.answerer_avatar_url : null,
      answererAiCallSummary: log.answerer_ai_call_summary,
      callerPhone: formatPhoneNumber(log.caller_phone),
      callerFullName: log.caller_full_name,
      callerUrl: log.caller_url,
      outcome,
      duration: log.duration,
      createdAt: dayjs(log.created_at).tz(timeZone).format("MM/DD/YYYY [at] h:mm a"),
      trackingNumberName: log.tracking_number.name,
      isNumberDeleted: !!log.tracking_number.deleted_at,
      voicemailUrl: log.voicemail_url,
      callSid: log.call_sid,
    };
  });

const mapTextLogs = (textLogs, timeZone) =>
  textLogs?.map((log) => ({
    textLogId: log.id,
    leadFullName: log.lead_full_name,
    leadUrl: log.lead_url,
    phoneNumber: formatPhoneNumber(log.phone_number),
    recipientFullName: log.recipient_full_name,
    recipientAvatarUrl: log.recipient_avatar_url,
    createdAtDate: dayjs(log.created_at).tz(timeZone).format("MM/DD/YYYY"),
    createdAtTime: dayjs(log.created_at).tz(timeZone).format("h:mm a"),
    trackingNumberName: log.tracking_number.name,
    trackingPhoneNumber: log.tracking_number?.number || "",
    messageUUID: log?.message_uuid || "",
    isNumberDeleted: !!log.tracking_number.deleted_at,
    message: log.message,
  }));

export default ({
  selectedTrackingNumbers,
  trackingNumbers,
  minDate,
  maxDate,
  offset,
  textoffset,
  logFilters,
  sort,
}) => {
  const currentUser = useCurrentUser();
  const [callLogs, setCallLogs] = useState([]);
  const [textLogs, setTextLogs] = useState([]);
  const [textlogsCount, setTextLogsCount] = useState(0);

  const [logsCount, setLogsCount] = useState(0);
  // Loading states
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [loadingTexts, setLoadingTexts] = useState(false);
  const [loadingCounts, setLoadingCounts] = useState(false);

  const limit = 25;

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingCalls(true);
        const callresults = await getCallLogs(
          getTrackingNumberIds(selectedTrackingNumbers, trackingNumbers),
          minDate,
          maxDate,
          sort[0],
          sort[1],
          offset,
          limit,
          logFilters,
        );
        setCallLogs(mapCallLogs(callresults, currentUser.account.timeZone));
      } finally {
        setLoadingCalls(false);
      }
    };

    load();
  }, [sort, selectedTrackingNumbers, minDate, maxDate, offset, logFilters]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingTexts(true);
        const textresults = await getTextLogs(
          getTrackingNumberIds(selectedTrackingNumbers, trackingNumbers),
          minDate,
          maxDate,
          textoffset,
          limit,
        );

        setTextLogs(mapTextLogs(textresults, currentUser.account.timeZone));
      } finally {
        setLoadingTexts(false);
      }
    };

    load();
  }, [sort, selectedTrackingNumbers, minDate, maxDate, textoffset]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingCounts(true);
        const logCount = await getCallLogCount(
          getTrackingNumberIds(selectedTrackingNumbers, trackingNumbers),
          minDate,
          maxDate,
          logFilters,
        );
        setLogsCount(logCount);
      } finally {
        setLoadingCounts(false);
      }
    };

    load();
  }, [selectedTrackingNumbers, minDate, maxDate, logFilters]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingCounts(true);
        const textlogCount = await getTextLogCount(
          getTrackingNumberIds(selectedTrackingNumbers, trackingNumbers),
          minDate,
          maxDate,
        );
        setTextLogsCount(textlogCount);
      } finally {
        setLoadingCounts(false);
      }
    };

    load();
  }, [selectedTrackingNumbers, minDate, maxDate]);

  return { callLogs, logsCount, textLogs, textlogsCount, loadingCalls, loadingTexts, loadingCounts };
};
